import type { QuestionAnalysis, QuestionCard, Topic } from "../types/journey";

export const SHORTCUT_QUESTIONS = [
  "我做的事情真的有意义吗？",
  "没有现成答案时，该不该尝试？",
  "努力很久没有结果，还要继续吗？",
  "当个人得失和集体需要发生冲突时，该如何选择？",
  "普通人的坚持能够改变什么？",
] as const;

export const ACTION_SHORTCUTS = ["我愿意继续", "我会犹豫", "我还不知道"] as const;

export const JOURNEY_STEPS = [
  { label: "首创", short: "01" },
  { label: "奋斗", short: "02" },
  { label: "奉献", short: "03" },
] as const;

export const TOPIC_LABELS: Record<Topic, string> = {
  innovation: "尝试",
  persistence: "坚持",
  responsibility: "责任",
  service: "服务",
  growth: "成长",
  uncertainty: "不确定",
  collaboration: "合作",
  other: "行动",
};

export const ALLOWED_ACTION_KEYWORDS = [
  "尝试",
  "坚持",
  "倾听",
  "学习",
  "责任",
  "改变",
  "合作",
  "行动",
] as const;

export const DEFAULT_DEMO_QUESTION = "做一件暂时看不到结果的事，还有意义吗？";

/* AI 降级时用户唯一能看到的解释。V1 与 V2 两条体验线共用同一句，
   避免出现"线上换成了模板文案但用户毫无感知"的情况。 */
export const AI_FALLBACK_NOTICE = "南湖起了一阵风，我们换一种方式继续。";

export const SENSITIVE_INPUT_REMINDER = "请勿输入姓名、手机号、身份证号、详细地址等个人敏感信息。";

export const FALLBACK_REFLECTIONS: Record<Topic, string> = {
  innovation:
    "你最开始问的是，当结果还没有出现时，一次尝试是否仍然值得。刚才的选择没有替你作答，却让问题多了一个角度：先把未知拆成可以验证的一小步，再让行动本身带来新的信息。",
  persistence:
    "你最开始问的是，努力很久仍看不到结果时是否要继续。刚才的选择把“继续”从一句判断，落回了节奏、方法与边界：保留在意的方向，也允许自己调整抵达它的方式。",
  responsibility:
    "你最开始问的是，个人得失与共同需要相遇时如何选择。刚才的回答没有给出统一答案，而是留下了一个更具体的提醒：看见他人，也看见自己的能力与边界，再决定下一步。",
  service:
    "你最开始问的是，怎样把在意的事情变成对身边人的帮助。刚才的选择让答案回到日常：先听见真实需要，再用自己能做到的方式提供一点可靠的支持。",
  growth:
    "你最开始问的是，成长是否一定要有清晰的路线。刚才的选择说明，成长也可以从一次诚实的试验开始：承认不会，再把下一步做得足够具体。",
  uncertainty:
    "你最开始问的是，无法预先知道结果时要不要出发。刚才的选择把不确定留在了现场，也把行动留了下来：不等所有答案出现，先为自己找到可回看的第一步。",
  collaboration:
    "你最开始问的是，一个人的选择怎样与他人形成更好的合力。刚才的回答提示我们，合作不是放弃判断，而是先把问题说清楚，再让不同的经验彼此靠近。",
  other:
    "你带来的问题没有被简化成一个结论。三次选择之后，它更像一面镜子：你在意什么、担心什么，以及明天愿意先做哪一件小事，都已经有了自己的方向。",
};

export const FALLBACK_QUESTIONS: Record<
  "innovation" | "persistence" | "responsibility",
  { first: QuestionAnalysis; second: QuestionCard }
> = {
  innovation: {
    first: {
      topic: "innovation",
      coreConflict: "尝试新的方法，还是继续沿用熟悉的路径",
      safeSummary: "你在想，当结果还不确定时，新的尝试是否值得开始。",
      questionOne: {
        context:
          "你发现一件沿用很久的事有改进空间。新的方法还没有人试过，也不能保证一次成功。此刻，你更愿意——",
        optionA: "先做一个小范围尝试，边走边修正",
        optionB: "再观察一段时间，先把熟悉的方法做稳",
      },
    },
    second: {
      context:
        "你已经迈出第一步，但几周过去，结果仍然很安静。身边的人开始问：是不是原来的方法更省力？你会——",
      optionA: "复盘这一步，保留方向并调整方法",
      optionB: "先暂停一下，确认这件事是否仍值得投入",
    },
  },
  persistence: {
    first: {
      topic: "persistence",
      coreConflict: "看不到即时结果时，如何与自己的节奏相处",
      safeSummary: "你在想，长期投入没有回声时，怎样判断下一步。",
      questionOne: {
        context:
          "你已经为一件重要的事投入了很久，却没有明显结果。熟悉的方法也没有给出新答案。此刻，你更愿意——",
        optionA: "换一个小方法，先完成能验证的一步",
        optionB: "继续沿用熟悉的节奏，再观察一段时间",
      },
    },
    second: {
      context:
        "你把目标拆成小步，但连续几次都没有达到预想的效果。你会——",
      optionA: "记录原因，再给自己一个可回看的周期",
      optionB: "换一种方式，先找回对这件事的真实感受",
    },
  },
  responsibility: {
    first: {
      topic: "responsibility",
      coreConflict: "个人安排与身边人的真实需要相遇时如何取舍",
      safeSummary: "你在想，照顾好自己与回应共同需要怎样同时成立。",
      questionOne: {
        context:
          "一个临时的共同任务来到面前，正好撞上你原本的安排。你还不清楚别人真正需要多少帮助。你会——",
        optionA: "先问清需要，再说出自己能承担的部分",
        optionB: "先守住原计划，等更明确的分工出现",
      },
    },
    second: {
      context:
        "你答应承担其中一部分，后来发现实际花费的时间比预想更多。你会——",
      optionA: "和伙伴重新分配，让事情继续向前",
      optionB: "把现状讲清楚，重新确认自己能做到的范围",
    },
  },
};
